import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Edit3,
  Trash2,
  Eye,
  EyeOff,
  Download,
  Upload,
  Search,
  Grid,
  Type,
  Layout,
  Layers,
  History,
  AlertTriangle,
  CheckCircle,
  Info,
  Database,
  Link as LinkIcon,
  Users,
  Shield,
  UserPlus,
  Mail,
  Key
} from 'lucide-react';
import {
  fetchCustomFields,
  createCustomField,
  updateCustomField,
  deleteCustomField
} from '../../services/api/uiConfigurationApi';

import {
  FieldConfig,
  canModifyUIConfig
} from '../../types/uiConfig';
import {
  mockSectionConfigs,
  mockTabConfigs,
  mockUIConfigChangeLogs,
  mockUsers,
  mockInvitations,
  mockRoleTemplates
} from '../../data/mockData';
import FieldConfigModal from '../../components/admin/FieldConfigModal';
import DynamicClientDetail from '../../components/admin/DynamicClientDetail';
import PageLayoutManager from '../../components/admin/PageLayoutManager';
import { StandardButton, StandardLoadingState, StandardErrorState } from '../../components/standard';

interface UIConfigurationManagerProps {
  currentUser?: any;
  userRole: string;
  initialTab?: string;
}

const UIConfigurationManager: React.FC<UIConfigurationManagerProps> = ({ userRole, initialTab = 'objects' }) => {
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedObject, setSelectedObject] = useState('client');
  const [selectedApplication, setSelectedApplication] = useState('all');
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [showDemo, setShowDemo] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [userSubTab, setUserSubTab] = useState('active');

  // Legacy mock data (for sections, tabs, etc - not migrated yet)
  const sections = mockSectionConfigs;
  const tabs = mockTabConfigs;
  const changeLogs = mockUIConfigChangeLogs;
  const users = mockUsers;
  const pendingInvitations = mockInvitations;
  const roleTemplates = mockRoleTemplates;

  const canModify = canModifyUIConfig(userRole);

  // React Query: Fetch custom fields with filters
  const { data: fieldsResponse, isLoading: isLoadingFields, error: fieldsError } = useQuery({
    queryKey: ['custom-fields', selectedApplication, selectedObject],
    queryFn: () => {
      const filters: any = {};
      if (selectedApplication !== 'all') {
        filters.application = selectedApplication;
      }
      if (selectedObject !== 'all') {
        filters.section = selectedObject;
      }
      return fetchCustomFields(filters);
    },
    staleTime: 60 * 1000 // Cache for 1 minute
  });

  const fields = fieldsResponse?.data || [];

  // Client-side search filtering
  const filteredFields = useMemo(() => {
    if (!searchQuery) return fields;

    const query = searchQuery.toLowerCase();
    return fields.filter(field =>
      field.label.toLowerCase().includes(query) ||
      field.fieldKey.toLowerCase().includes(query) ||
      field.type.toLowerCase().includes(query) ||
      field.application.toLowerCase().includes(query) ||
      field.section.toLowerCase().includes(query)
    );
  }, [fields, searchQuery]);

  // React Query: Create field mutation
  const createFieldMutation = useMutation({
    mutationFn: createCustomField,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
      setShowFieldModal(false);
      setEditingField(null);
    },
    onError: (error: any) => {
      console.error('Failed to create field:', error);
      alert(error.message || 'Failed to create field');
    }
  });

  // React Query: Update field mutation
  const updateFieldMutation = useMutation({
    mutationFn: ({ fieldId, updates }: { fieldId: string; updates: any }) =>
      updateCustomField(fieldId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
      setShowFieldModal(false);
      setEditingField(null);
    },
    onError: (error: any) => {
      console.error('Failed to update field:', error);
      alert(error.message || 'Failed to update field');
    }
  });

  // React Query: Delete field mutation
  const deleteFieldMutation = useMutation({
    mutationFn: deleteCustomField,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
    },
    onError: (error: any) => {
      console.error('Failed to delete field:', error);
      alert(error.message || 'Failed to delete field');
    }
  });

  const objectTypes = [
    { value: 'client', label: 'Client' },
    { value: 'task', label: 'Task' },
    { value: 'user', label: 'User' },
    { value: 'case', label: 'Case' },
    { value: 'audit', label: 'Audit' },
    { value: 'freeze', label: 'Freeze' },
    { value: 'paw', label: 'PAW' }
  ];

  const applications = [
    { value: 'all', label: 'All Applications' },
    { value: 'global', label: 'Global/Shared' },
    { value: 'cybersweep', label: 'CyberSweep' },
    { value: 'credit-repair', label: 'Credit Repair' },
    { value: 'id-theft', label: 'ID Theft Recovery' },
    { value: 'credit-development', label: 'Credit Development' },
    { value: 'credit-block', label: 'Credit Block' }
  ];

  const tabs_config = [
    { id: 'objects', label: 'Custom Objects', icon: Database, count: objectTypes.length },
    { id: 'fields', label: 'Custom Fields', icon: Type, count: fields.length },
    { id: 'sections', label: 'Sections', icon: Layers, count: sections.length },
    { id: 'tabs', label: 'Tabs', icon: Layout, count: tabs.length },
    { id: 'pages', label: 'Page Layouts', icon: Grid, count: 0 },
    { id: 'relationships', label: 'Relationships', icon: LinkIcon, count: 0 },
    { id: 'users', label: 'Users & Roles', icon: Users, count: 0 },
    { id: 'permissions', label: 'Field Permissions', icon: Shield, count: 0 },
    { id: 'changes', label: 'Change History', icon: History, count: changeLogs.length }
  ];

  const getFilteredFields = () => {
    // Use the memoized filteredFields from React Query
    return filteredFields;
  };

  const getFilteredSections = () => {
    return sections.filter(section => {
      const matchesSearch = section.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           section.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesObject = selectedObject === 'all' || section.objectType === selectedObject;
      const matchesApp = selectedApplication === 'all' ||
                        section.applicationId === selectedApplication ||
                        (selectedApplication === 'global' && section.isShared === true);
      return matchesSearch && matchesObject && matchesApp;
    });
  };

  const getFilteredTabs = () => {
    return tabs.filter(tab => {
      const matchesSearch = tab.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           tab.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesObject = selectedObject === 'all' || tab.objectType === selectedObject;
      const matchesApp = selectedApplication === 'all' ||
                        tab.applicationId === selectedApplication ||
                        (selectedApplication === 'global' && tab.isShared === true);
      return matchesSearch && matchesObject && matchesApp;
    });
  };

  const renderFieldsTab = () => {
    // Loading state
    if (isLoadingFields) {
      return (
        <StandardLoadingState message="Loading custom fields..." />
      );
    }

    // Error state
    if (fieldsError) {
      return (
        <StandardErrorState
          title="Failed to load custom fields"
          message="Please try again or contact support if the problem persists."
        />
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Field Configuration</h3>
            <p className="text-sm text-gray-600">Manage custom fields and their properties</p>
          </div>
          {canModify && (
            <StandardButton
              variant="primary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={handleCreateField}
              disabled={createFieldMutation.isPending}
            >
              {createFieldMutation.isPending ? 'Creating...' : 'Add Field'}
            </StandardButton>
          )}
        </div>

        {/* Developer indicator */}
        {process.env.NODE_ENV === 'development' && filteredFields.length > 0 && (
          <div style={{
            padding: '0.75rem 1rem',
            background: '#E3F2FD',
            borderRadius: '6px',
            fontSize: '0.875rem',
            color: '#1565C0',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            border: '1px solid #90CAF9'
          }}>
            <span>ℹ️</span>
            <span>
              <strong>Development Mode:</strong> Showing {filteredFields.length} custom field{filteredFields.length !== 1 ? 's' : ''} from mock data.
              Connect backend API to see real-time data.
            </span>
          </div>
        )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search fields..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <select
            value={selectedApplication}
            onChange={(e) => setSelectedApplication(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {applications.map(app => (
              <option key={app.value} value={app.value}>{app.label}</option>
            ))}
          </select>
          <select
            value={selectedObject}
            onChange={(e) => setSelectedObject(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Objects</option>
            {objectTypes.map(obj => (
              <option key={obj.value} value={obj.value}>{obj.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Fields Table */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Field</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Application</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Section</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Required</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Visible</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Custom</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
            {getFilteredFields().map((field) => (
              <tr key={field.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{field.label}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{field.fieldKey}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {field.type}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      field.application === 'global' ? 'bg-green-100 text-green-800' :
                      field.application === 'cybersweep' ? 'bg-purple-100 text-purple-800' :
                      field.application === 'credit-repair' ? 'bg-orange-100 text-orange-800' :
                      field.application === 'id-theft' ? 'bg-red-100 text-red-800' :
                      field.application === 'credit-development' ? 'bg-blue-100 text-blue-800' :
                      field.application === 'credit-block' ? 'bg-pink-100 text-pink-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {applications.find(app => app.value === field.application)?.label || field.application}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {field.section}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {field.isRequired ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <div className="h-5 w-5" />
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {field.isActive ? (
                    <Eye className="h-5 w-5 text-green-500" />
                  ) : (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {!field.isSystem ? (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      Custom
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Standard
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center space-x-2">
                    {canModify && (
                      <>
                        <button
                          onClick={() => handleEditField(field as any)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        {!field.isSystem && (
                          <button className="text-red-600 hover:text-red-800">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </>
                    )}
                    <button className="text-gray-600 hover:text-gray-800">
                      <Eye className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

  const renderSectionsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Section Configuration</h3>
          <p className="text-sm text-gray-600">Organize fields into logical sections</p>
        </div>
        {canModify && (
          <StandardButton
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Add Section
          </StandardButton>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {getFilteredSections().map((section) => (
          <div key={section.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <Layers className="h-8 w-8 text-blue-600 mr-3" />
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{section.label}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{section.name}</p>
                </div>
              </div>
              {section.isCustom && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Custom
                </span>
              )}
            </div>

            {section.description && (
              <p className="text-sm text-gray-600 mb-4">{section.description}</p>
            )}

            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Application:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  section.applicationId === 'global' ? 'bg-green-100 text-green-800' :
                  section.applicationId === 'cybersweep' ? 'bg-purple-100 text-purple-800' :
                  section.applicationId === 'credit-repair' ? 'bg-orange-100 text-orange-800' :
                  section.applicationId === 'id-theft' ? 'bg-red-100 text-red-800' :
                  section.applicationId === 'credit-development' ? 'bg-blue-100 text-blue-800' :
                  section.applicationId === 'credit-block' ? 'bg-pink-100 text-pink-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {applications.find(app => app.value === section.applicationId)?.label || section.applicationId}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Object Type:</span>
                <span className="font-medium">{section.objectType}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Columns:</span>
                <span className="font-medium">{section.columns}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Collapsible:</span>
                <span className="font-medium">{section.isCollapsible ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Visible:</span>
                <span className="font-medium">{section.isVisible ? 'Yes' : 'No'}</span>
              </div>
            </div>

            {canModify && (
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  Edit Section
                </button>
                <div className="flex space-x-2">
                  <button className="text-gray-600 hover:text-gray-800">
                    <Eye className="h-4 w-4" />
                  </button>
                  {section.isCustom && (
                    <button className="text-red-600 hover:text-red-800">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderTabsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Tab Configuration</h3>
          <p className="text-sm text-gray-600">Manage page tabs and their content</p>
        </div>
        {canModify && (
          <StandardButton
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Add Tab
          </StandardButton>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tab</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Application</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Sections</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Permissions</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Visible</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Custom</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
            {getFilteredTabs().map((tab) => (
              <tr key={tab.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Layout className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{tab.label}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{tab.name}</div>
                      {tab.description && (
                        <div className="text-xs text-gray-400">{tab.description}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      tab.applicationId === 'global' ? 'bg-green-100 text-green-800' :
                      tab.applicationId === 'cybersweep' ? 'bg-purple-100 text-purple-800' :
                      tab.applicationId === 'credit-repair' ? 'bg-orange-100 text-orange-800' :
                      tab.applicationId === 'id-theft' ? 'bg-red-100 text-red-800' :
                      tab.applicationId === 'credit-development' ? 'bg-blue-100 text-blue-800' :
                      tab.applicationId === 'credit-block' ? 'bg-pink-100 text-pink-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {applications.find(app => app.value === tab.applicationId)?.label || tab.applicationId}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">{tab.objectType}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-gray-100">
                    {tab.sections.length} sections
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {tab.sections.join(', ')}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-gray-100">
                    View: {tab.permissions?.view?.length || 0}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Edit: {tab.permissions?.edit?.length || 0}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {tab.isVisible ? (
                    <Eye className="h-5 w-5 text-green-500" />
                  ) : (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {tab.isCustom ? (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      Custom
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Standard
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center space-x-2">
                    {canModify && (
                      <>
                        <button className="text-blue-600 hover:text-blue-800">
                          <Edit3 className="h-4 w-4" />
                        </button>
                        {tab.isCustom && (
                          <button className="text-red-600 hover:text-red-800">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </>
                    )}
                    <button className="text-gray-600 hover:text-gray-800">
                      <Eye className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderObjectsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Custom Objects</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Manage custom objects for Credit Management</p>
        </div>
        {canModify && (
          <StandardButton
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Create Object
          </StandardButton>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Object Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">API Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Fields</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {objectTypes.map((obj) => (
              <tr key={obj.value} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{obj.label}</td>
                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{obj.value}</td>
                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">Standard object for {obj.label} management</td>
                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {fields.filter(f => f.section === obj.value).length} fields
                </td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full">
                    Active
                  </span>
                </td>
                <td className="px-6 py-4 text-right text-sm">
                  <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderRelationshipsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Object Relationships</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Define relationships between objects (Lookup, Master-Detail)</p>
        </div>
        {canModify && (
          <StandardButton
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Add Relationship
          </StandardButton>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
        <LinkIcon className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <p className="text-gray-500 dark:text-gray-400">No relationships configured</p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">Use Object Manager to define Lookup and Master-Detail relationships between objects</p>
      </div>
    </div>
  );

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'system_admin': return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
      case 'product_manager': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
      case 'team_user': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getDepartmentColor = (department: string) => {
    switch (department) {
      case 'all': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300';
      case 'credit_management': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
      case 'cybersweep': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300';
      case 'credit_block': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const renderUsersTab = () => {
    const userSubTabs = [
      { id: 'active', label: 'Active Users', count: users.filter(u => u.isActive).length },
      { id: 'invitations', label: 'Invitations', count: pendingInvitations.length },
      { id: 'roles', label: 'Roles & Permissions', count: roleTemplates.length }
    ];

    return (
      <div className="space-y-6">
        {/* Sub-tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {userSubTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setUserSubTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  userSubTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                {tab.label}
                <span className="ml-2 py-0.5 px-2.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Active Users Tab */}
        {userSubTab === 'active' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">User Management</h3>
              {canModify && (
                <StandardButton
                  variant="primary"
                  leftIcon={<UserPlus className="h-4 w-4" />}
                >
                  Invite User
                </StandardButton>
              )}
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="h-5 w-5 absolute left-3 top-3 text-gray-400 dark:text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </div>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">All Roles</option>
                  <option value="system_admin">System Admin</option>
                  <option value="product_manager">Product Manager</option>
                  <option value="team_user">Team User</option>
                </select>
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">All Departments</option>
                  <option value="all">All Departments</option>
                  <option value="credit_management">Credit Management</option>
                  <option value="cybersweep">CyberSweep</option>
                  <option value="credit_block">Credit Block</option>
                </select>
              </div>
            </div>

            {/* Users Table */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Department</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Login</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                  {users.filter(u => u.isActive).map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {user.avatar ? (
                            <img
                              className="h-10 w-10 rounded-full"
                              src={user.avatar}
                              alt={`${user.firstName} ${user.lastName}`}
                            />
                          ) : (
                            <div className="h-10 w-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                              {user.firstName[0]}{user.lastName[0]}
                            </div>
                          )}
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                            <div className="text-xs text-gray-400 dark:text-gray-500">{user.title}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                          {user.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDepartmentColor(user.department)}`}>
                          {user.department.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(user.isActive)}`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {user.lastLogin}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center space-x-2">
                          <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300">
                            <Key className="h-4 w-4" />
                          </button>
                          <button className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Invitations Tab */}
        {userSubTab === 'invitations' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Pending Invitations</h3>
              {canModify && (
                <StandardButton
                  variant="primary"
                  leftIcon={<Mail className="h-4 w-4" />}
                >
                  Send Invitation
                </StandardButton>
              )}
            </div>

            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Department</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Invited By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Expires</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                  {pendingInvitations.map((invitation) => (
                    <tr key={invitation.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                        {invitation.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(invitation.role)}`}>
                          {invitation.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDepartmentColor(invitation.department)}`}>
                          {invitation.department.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {invitation.invitedBy}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {invitation.expiresDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center space-x-2">
                          <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                            <Mail className="h-4 w-4" />
                          </button>
                          <button className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Roles & Permissions Tab */}
        {userSubTab === 'roles' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Roles & Permissions</h3>
              {canModify && (
                <StandardButton
                  variant="primary"
                  leftIcon={<Plus className="h-4 w-4" />}
                >
                  Create Role
                </StandardButton>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {roleTemplates.map((role) => (
                <div key={role.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className={`p-2 ${getRoleColor(role.id)} rounded-lg`}>
                        <Shield className="h-5 w-5" />
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{role.name}</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{users.filter(u => u.role === role.id).length} users</p>
                      </div>
                    </div>
                    <button className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                      <Edit3 className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{role.description}</p>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Key Permissions:</p>
                    <div className="flex flex-wrap gap-1">
                      {(role as any).permissions?.slice(0, 3).map((perm: string, idx: number) => (
                        <span key={idx} className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                          {perm}
                        </span>
                      ))}
                      {(role as any).permissions?.length > 3 && (
                        <span className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400">
                          +{(role as any).permissions.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPermissionsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Field-Level Permissions</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Configure field-level security and visibility by role</p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
        <Shield className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <p className="text-gray-500 dark:text-gray-400">No field permissions configured</p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">Configure field-level security in Security & Access settings</p>
      </div>
    </div>
  );

  const renderPagesTab = () => <PageLayoutManager />;

  const renderChangesTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Configuration Changes</h3>
          <p className="text-sm text-gray-600">Track all UI configuration modifications</p>
        </div>
        <StandardButton
          variant="secondary"
          leftIcon={<Download className="h-4 w-4" />}
        >
          Export Changes
        </StandardButton>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Change</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Object</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Impact</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Changed By</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
            {changeLogs.map((log) => (
              <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {log.changeType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                    <div className="text-sm text-gray-900 dark:text-gray-100 mt-1">{log.objectId}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {log.objectType}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    log.impact === 'high' ? 'bg-red-100 text-red-800' :
                    log.impact === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {log.impact}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {log.changedBy}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {new Date(log.changeDate).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <button className="text-blue-600 hover:text-blue-800">
                    <Eye className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const handleSaveField = (fieldConfig: FieldConfig) => {
    if (editingField) {
      // Update existing field with React Query
      updateFieldMutation.mutate({
        fieldId: fieldConfig.id,
        updates: fieldConfig as any
      });
    } else {
      // Create new field with React Query
      createFieldMutation.mutate(fieldConfig as any);
    }
  };

  const handleEditField = (field: any) => {
    setEditingField(field);
    setShowFieldModal(true);
  };

  const handleCreateField = () => {
    setEditingField(null);
    setShowFieldModal(true);
  };

  // Show demo if requested
  if (showDemo) {
    return (
      <DynamicClientDetail
        clientData={{
          firstName: 'Demo',
          lastName: 'Client',
          email: 'demo@client.com',
          phone: '(555) 123-4567',
          status: 'active',
          creditScore: 680,
          enrollmentDate: '2025-03-15',
          services: ['credit_repair', 'credit_monitoring']
        }}
        userRole={userRole}
        onBack={() => setShowDemo(false)}
      />
    );
  }

  if (!canModify && userRole !== 'product_manager') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <AlertTriangle className="h-16 w-16 mx-auto text-red-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">
            You don't have permission to access the UI Configuration Manager.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            This feature is available to System Administrators only.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">UI Configuration Manager</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">Customize fields, sections, and layouts without developer intervention</p>
            </div>
            <div className="flex items-center space-x-4">
              <StandardButton
                variant="primary"
                leftIcon={<Eye className="h-4 w-4" />}
                onClick={() => setShowDemo(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                View Demo
              </StandardButton>
              {canModify && (
                <>
                  <StandardButton
                    variant="secondary"
                    leftIcon={<Upload className="h-4 w-4" />}
                  >
                    Import Config
                  </StandardButton>
                  <StandardButton
                    variant="secondary"
                    leftIcon={<Download className="h-4 w-4" />}
                  >
                    Export Config
                  </StandardButton>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-8">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300">System Administrator Controls</h3>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Make real-time changes to field properties, section layouts, and page configurations.
                Changes are applied immediately and tracked in the change history.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <Type className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Fields</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{fields.length}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-400">{fields.filter(f => !f.isSystem).length} custom</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <Layers className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Sections</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{sections.length}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-400">{sections.filter(s => s.isCustom).length} custom</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <Layout className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Tabs</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{tabs.length}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-400">{tabs.filter(t => t.isCustom).length} custom</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <History className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Recent Changes</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{changeLogs.length}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-400">last 30 days</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="border-b border-gray-200 dark:border-gray-600">
            <nav className="flex space-x-8 px-6">
              {tabs_config.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                    {tab.count > 0 && (
                      <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 py-1 px-2 rounded-full text-xs">
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'objects' && renderObjectsTab()}
            {activeTab === 'fields' && renderFieldsTab()}
            {activeTab === 'sections' && renderSectionsTab()}
            {activeTab === 'tabs' && renderTabsTab()}
            {activeTab === 'pages' && renderPagesTab()}
            {activeTab === 'relationships' && renderRelationshipsTab()}
            {activeTab === 'users' && renderUsersTab()}
            {activeTab === 'permissions' && renderPermissionsTab()}
            {activeTab === 'changes' && renderChangesTab()}
          </div>
        </div>
      </div>

      {/* Field Configuration Modal */}
      <FieldConfigModal
        isOpen={showFieldModal}
        onClose={() => {
          setShowFieldModal(false);
          setEditingField(null);
        }}
        onSave={handleSaveField}
        field={editingField}
        existingFields={fields as any}
        sections={sections.map(s => ({
          id: s.id,
          label: s.label,
          applicationId: s.applicationId,
          isShared: s.isShared,
          sharedWith: s.sharedWith
        }))}
      />
    </div>
  );
};

export default UIConfigurationManager;